import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowRight, DollarSign, TrendingUp, Users, Zap } from 'lucide-react';
import SeoHead from '../components/SeoHead';
import api from '../utils/api';

const TIERS = [
  { name: 'Bronze', rate: '10%', refs: '0+', color: 'text-amber-600' },
  { name: 'Silver', rate: '15%', refs: '10+', color: 'text-gray-400' },
  { name: 'Gold', rate: '20%', refs: '25+', color: 'text-yellow-400' },
  { name: 'Platinum', rate: '25%', refs: '50+', color: 'text-cyan-300' },
];

export default function AffiliateRecruit() {
  const { code } = useParams();
  const [affiliate, setAffiliate] = useState(null);

  useEffect(() => {
    if (code) {
      api.get(`/affiliate/public/${code}`).then(r => setAffiliate(r.data)).catch(() => {});
    }
  }, [code]);

  return (
    <>
      <SeoHead title="Join Our Affiliate Program — AI Ghostwriter Pro" description="Earn 10-25% recurring commission promoting AI Ghostwriter Pro. No startup costs, real-time tracking, monthly payouts." />
      <section className="pt-28 pb-20 md:pt-40 md:pb-32">
        <div className="max-w-[1000px] mx-auto px-4 sm:px-6 md:px-12 lg:px-20">
          {affiliate && (
            <div className="text-center mb-6">
              <p className="text-xs font-sans uppercase tracking-[0.2em] text-primary font-bold">Invited by {affiliate.name} ({affiliate.tier})</p>
            </div>
          )}
          <div className="text-center mb-12">
            <h1 className="font-serif text-4xl md:text-5xl tracking-tight mb-4" data-testid="recruit-title">
              Earn <span className="text-primary">Recurring Income</span> Promoting Books
            </h1>
            <p className="text-muted-foreground max-w-xl mx-auto">Join our affiliate program and earn 10-25% on every subscription. No startup costs.</p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-12">
            {TIERS.map(t => (
              <div key={t.name} className="bg-white/5 border border-white/10 rounded-sm p-4 text-center" data-testid={`tier-${t.name.toLowerCase()}`}>
                <p className={`font-serif text-lg ${t.color}`}>{t.name}</p>
                <p className="text-2xl font-serif text-primary">{t.rate}</p>
                <p className="text-[10px] text-muted-foreground uppercase tracking-wider">{t.refs} referrals</p>
              </div>
            ))}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-12">
            {[
              { icon: DollarSign, title: 'Recurring Commissions', desc: 'Earn on every subscription payment, not just the first. Monthly or annual.' },
              { icon: TrendingUp, title: 'Automatic Tier Upgrades', desc: 'Your commission rate increases as you refer more customers.' },
              { icon: Users, title: '90-Day Cookie', desc: 'Your referrals are tracked for 90 days — plenty of time to convert.' },
              { icon: Zap, title: 'Real-Time Dashboard', desc: 'Track clicks, signups, conversions, and earnings in real time.' },
            ].map((item, i) => (
              <div key={i} className="bg-white/5 border border-white/10 rounded-sm p-6 flex gap-4">
                <item.icon className="w-5 h-5 text-primary shrink-0 mt-0.5" strokeWidth={1.5} />
                <div>
                  <h3 className="font-serif text-sm mb-1">{item.title}</h3>
                  <p className="text-xs text-muted-foreground">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>

          <div className="text-center">
            <Link to="/affiliate" data-testid="recruit-apply-btn" className="inline-flex items-center gap-2 rounded-sm px-8 py-4 bg-primary text-primary-foreground font-semibold tracking-wide transition-all duration-300 shadow-[0_0_15px_rgba(212,175,55,0.3)] hover:bg-white hover:text-black">
              Apply Now <ArrowRight className="w-4 h-4" />
            </Link>
            <p className="text-xs text-muted-foreground mt-3">Free to join. Start earning today.</p>
          </div>
        </div>
      </section>
    </>
  );
}
